import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { JobState } from '../../enums/job-state.enum';
import { Job } from '../../interfaces/job';
import * as JobActions from './jobs.actions';

export const jobsFeatureKey = 'jobs';

export interface State extends EntityState<Job> {
  // additional entities state properties
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: any;
  initiativeID: number;
}

export const adapter: EntityAdapter<Job> = createEntityAdapter<Job>({
  sortComparer: (a, b) => a.index - b.index,
});

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
  initiativeID: null, // TODO change to initiativeJob itself
});

export const jobsReducer = createReducer(
  initialState,
  on(JobActions.loadJobs, (state) => ({ ...state, loading: true })),
  on(JobActions.loadJobsSuccess, (state, action) =>
    adapter.upsertMany(action.jobs, { ...state, loading: false, initiativeID: action.initiativeID })
  ),
  on(JobActions.loadJobsFailure, (state, action) => ({ ...state, error: action.error, loading: false })),
  on(JobActions.createJob, (state, action) => ({ action, ...state, creating: true })),
  on(JobActions.createJobSuccess, (state, action) => adapter.upsertOne(action.job, { ...state, creating: false })),
  on(JobActions.createJobFailure, (state, action) => ({ ...state, error: action.error, creating: false })),
  on(JobActions.updateJob, (state, action) => ({ action, ...state, updating: true })),
  on(JobActions.updateJobSuccess, (state, action) => adapter.upsertOne(action.job, { ...state, updating: false })),
  on(JobActions.updateJobFailure, (state, action) => ({ ...state, error: action.error, updating: false })),
  on(JobActions.deleteJob, (state) => ({ ...state, deleting: true })),
  on(JobActions.deleteJobSuccess, (state, action) => adapter.removeOne(action.id, { ...state, deleting: true })),
  on(JobActions.deleteJobFailure, (state, action) => ({ ...state, error: action.error, deleting: true })),
  on(JobActions.sortJobs, (state) => ({ ...state })),
  on(JobActions.sortJobsSuccess, (state, action) => adapter.setAll(action.jobs, state)),
  on(JobActions.sortJobsFailure, (state, action) => ({ ...state, error: action.error })),
  on(JobActions.deleteConnectionForJob, (state) => ({ ...state })),
  on(JobActions.deleteConnectionForJobSuccess, (state, action) =>
    adapter.upsertOne(
      {
        ...action.job,
        hasConnection: false,
      },
      { ...state, updating: false }
    )
  ),
  on(JobActions.deleteConnectionForJobFailure, (state, action) => ({ ...state, error: action.error })),
  on(JobActions.clearJobs, (state) => adapter.removeAll(state))
);

export const selectJobState = createFeatureSelector<State>(jobsFeatureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors(selectJobState);

export const selectJobsAll = selectAll;
export const selectJobsTotal = selectTotal;
export const selectJobsEntities = selectEntities;
export const selectJobsIds = selectIds;
export const selectJobsAllSortedbyIndex = createSelector(selectJobsAll, (jobs) =>
  jobs.sort((a, b) => a.index - b.index)
);

export const selectInitiativeID = createSelector(selectJobState, (state) => state.initiativeID);
export const selectJobsLoading = createSelector(selectJobState, (state) => state.loading);
export const selectJobsDrafts = createSelector(selectJobsAllSortedbyIndex, (jobs) =>
  jobs.filter((job) => job.state === JobState.DRAFT)
);
export const selectJobsOpen = createSelector(selectJobsAllSortedbyIndex, (jobs) =>
  jobs.filter((job) => job.state === JobState.OPEN)
);
export const selectJobsClosed = createSelector(selectJobsAllSortedbyIndex, (jobs) =>
  jobs.filter((job) => job.state === JobState.CLOSED)
);
export const selectJobsOpenAndClosed = createSelector(selectJobsAllSortedbyIndex, (jobs) =>
  jobs.filter((job) => job.state === JobState.OPEN || job.state === JobState.CLOSED)
);
export const selectJobsWithConnection = createSelector(selectJobsAllSortedbyIndex, (jobs) =>
  jobs.filter((job) => job.hasConnection)
);
